import { Formik } from 'formik';
import { GraphQLError } from 'graphql';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { Box } from 'reflexbox';

import {
	MfaMethod,
	type MfaSetupChallenge,
	type UpdateSessionResponse,
	useAssociateTotpTokenMutation,
	useMfaSetupChallengeMutation,
} from '@treams/treams-network';
import {
	GraphQLErrorMessage,
	Paragraph,
	Separator,
	Spinner,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import SecretKeyVerification from '../../../../../../../src/personal-settings/mfa/SecretKeyVerification';
import PublicForm from '../../../components/authentication/PublicForm';

export default function TotpMfaSetupChallenge(props: {
	login: string;
	context: string;
	challenge: MfaSetupChallenge;
	onResponse: (response: UpdateSessionResponse) => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation();

	const { context, challenge, onCancel } = props;

	const [commit] = useMfaSetupChallengeMutation();
	const [getSecretKey] = useAssociateTotpTokenMutation();

	const [session, setSession] = useState<string>(context);
	const [secretKey, setSecretKey] = useState<string | undefined>(undefined);
	const [error, setError] = useState<GraphQLError | undefined>(undefined);

	useEffectOnce(() => {
		async function loadSecretKey() {
			try {
				const result = await getSecretKey({
					variables: {
						input: {
							session,
						},
					},
				});

				setSecretKey(result.data?.associateTotpToken.secretCode);
				setSession(result.data?.associateTotpToken.session ?? context);
			} catch (errors) {
				setError(errors.graphQLErrors[0]);
			}
		}

		loadSecretKey();
	});

	const onSubmit = useCallback(
		async (session?: string) => {
			try {
				const { data } = await commit({
					variables: {
						input: {
							response: {
								context: session ?? context,
								mfaSetup: {
									username: challenge.username,
									method: MfaMethod.Totp,
								},
							},
						},
					},
				});

				props.onResponse(data!.updateSession);
			} catch (errors) {
				setError(errors.graphQLErrors[0]);
			}
		},
		[commit, context, challenge.username, props],
	);

	return (
		<Formik initialValues={{}} onSubmit={() => {}}>
			<PublicForm>
				<Box>
					<Box>
						<Subheading mb={`${tokens.gap.group}px`}>{t('auth.login.mfa.setup.title')}</Subheading>
						<Paragraph mb={`${tokens.gap.section}px`}>
							{t('auth.login.mfa.setup.description')}
						</Paragraph>
						<GraphQLErrorMessage error={error} />
					</Box>
					<Separator mt={3} mb={4} />
					{secretKey ? (
						<SecretKeyVerification
							secretKey={secretKey}
							onDismiss={onCancel}
							onComplete={onSubmit}
							challenge={{
								session,
								username: props.login,
							}}
						/>
					) : (
						<Spinner centered />
					)}
				</Box>
			</PublicForm>
		</Formik>
	);
}
